import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";

const mainNavLinks = [
  { label: "Home", href: "/", icon: "ri-home-line" },
  { label: "Services", href: "/services", icon: "ri-tools-line" },
  { label: "Karnataka Cities", href: "/karnataka-cities", icon: "ri-map-pin-2-line" },
  { label: "FAQs", href: "/faq", icon: "ri-questionnaire-line" },
  { label: "Contact Us", href: "/contact", icon: "ri-contacts-line" },
];

const profileLinks = [
  { label: "About Us", href: "/about" },
  { label: "The Visionary", href: "/visionary" },
];

const usefulLinks = [
  { label: "Pricing", href: "/pricing" },
  { label: "How It Works", href: "/how-it-works" },
  { label: "AI Technology", href: "/ai-technology" },
  { label: "Strategies", href: "/strategies" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Press", href: "/press" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [usefulOpen, setUsefulOpen] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [mobileUsefulOpen, setMobileUsefulOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const usefulDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { settings } = useSettings();
  const displayPhone = settings.general.primary_phone || '+91 90350 63455';
  const phoneRaw = settings.general.primary_phone_raw || '+919035063455';
  const displayEmail = settings.general.contact_email || 'support@cashmyproperty.ai';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
    setUsefulOpen(false);
    setMobileProfileOpen(false);
    setMobileUsefulOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
      if (usefulDropdownRef.current && !usefulDropdownRef.current.contains(e.target as Node)) {
        setUsefulOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (href: string) => location.pathname === href;
  const isProfileActive = () => profileLinks.some((l) => location.pathname === l.href);
  const isUsefulActive = () => usefulLinks.some((l) => location.pathname === l.href);

  const navLinkClass = (active: boolean) =>
    `flex items-center gap-1.5 text-xs xl:text-sm font-medium transition-colors whitespace-nowrap px-2 xl:px-3 py-2 rounded-lg cursor-pointer ${
      active
        ? "text-primary-500 bg-primary-500/10"
        : "text-foreground-700 hover:text-primary-500 hover:bg-primary-500/5"
    }`;

  const mobileLinkClass = (active: boolean) =>
    `flex items-center gap-3 py-3 px-2 text-sm tracking-wide rounded-lg transition-colors cursor-pointer ${
      active
        ? "text-primary-500 font-medium bg-primary-500/10"
        : "text-foreground-700 hover:text-primary-500 hover:bg-primary-500/5"
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? "bg-background-50/95 backdrop-blur-xl border-b border-primary-900/30"
          : "bg-background-50/80 backdrop-blur-md border-b border-primary-900/10"
      }`}
    >
      {/* Gold Top Bar */}
      <div className="bg-primary-500 text-background-50">
        <div className="container mx-auto flex items-center justify-between px-4 lg:px-8 py-1.5">
          <div className="hidden sm:flex items-center gap-5 text-xs">
            <a href={`tel:${phoneRaw}`} className="flex items-center gap-1.5 hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line" />
              {displayPhone}
            </a>
            <a href={`mailto:${displayEmail}`} className="flex items-center gap-1.5 hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-mail-line" />
              {displayEmail}
            </a>
          </div>
          <div className="flex items-center gap-3 text-xs sm:ml-auto">
            <span className="hidden md:inline text-background-100/80">Unlock your land&apos;s hidden value</span>
            <Link
              to="/feasibility-report"
              className="px-3 py-1 rounded-full bg-background-50 text-primary-600 font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer text-[11px]"
            >
              Feasible AI Report &rarr;
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto flex items-center justify-between py-3 px-4 lg:px-8">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 group"
        >
          {settings.general.logo_url ? (
            <img
              src={settings.general.logo_url}
              alt={settings.general.site_name || 'CashMyProperty.ai'}
              className="h-8 w-auto max-w-[140px] object-contain"
            />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <span className="text-background-50 font-display font-bold text-sm">C</span>
            </div>
          )}
          {settings.general.show_site_name !== false && (
            <span className="font-display font-bold text-lg text-foreground-950">
              CashMyProperty<span className="text-primary-500">.ai</span>
            </span>
          )}
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1 xl:gap-1.5">
          {/* Home */}
          <Link to="/" className={navLinkClass(isActive("/"))}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-home-line text-sm leading-none" />
            </div>
            Home
          </Link>

          {/* Profile dropdown */}
          <div
            ref={profileDropdownRef}
            className="relative"
            onMouseEnter={() => setProfileOpen(true)}
            onMouseLeave={() => setProfileOpen(false)}
          >
            <button
              className={`flex items-center gap-1 text-xs xl:text-sm font-medium transition-colors whitespace-nowrap px-2 xl:px-3 py-2 rounded-lg cursor-pointer ${
                isProfileActive()
                  ? "text-primary-500 bg-primary-500/10"
                  : "text-foreground-700 hover:text-primary-500 hover:bg-primary-500/5"
              }`}
              onClick={() => setProfileOpen(!profileOpen)}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-line text-sm leading-none" />
              </div>
              Profile
              <div className="w-3 h-3 flex items-center justify-center">
                <i
                  className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
                    profileOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>

            {profileOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-44 pt-2">
                <div className="py-2 bg-background-50 border border-primary-900/30 rounded-lg shadow-lg animate-dropdown">
                  {profileLinks.map((link) => (
                    <Link
                      key={link.label}
                      to={link.href}
                      className={`block px-4 py-2 text-sm tracking-wide transition-colors cursor-pointer ${
                        isActive(link.href)
                          ? "text-primary-500 font-semibold bg-primary-500/10"
                          : "text-foreground-700 hover:text-primary-500 hover:bg-primary-500/5"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Remaining main links (after Profile) */}
          {mainNavLinks.filter((l) => l.href !== "/" && (l.href !== "/karnataka-cities" || settings.features.karnataka_cities_page !== false)).map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className={navLinkClass(isActive(link.href))}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${link.icon} text-sm leading-none`} />
              </div>
              {link.label}
            </Link>
          ))}

          {/* Useful Links dropdown */}
          <div
            ref={usefulDropdownRef}
            className="relative"
            onMouseEnter={() => setUsefulOpen(true)}
            onMouseLeave={() => setUsefulOpen(false)}
          >
            <button
              className={`flex items-center gap-1 text-xs xl:text-sm font-medium transition-colors whitespace-nowrap px-2 xl:px-3 py-2 rounded-lg cursor-pointer ${
                isUsefulActive()
                  ? "text-primary-500 bg-primary-500/10"
                  : "text-foreground-700 hover:text-primary-500 hover:bg-primary-500/5"
              }`}
              onClick={() => setUsefulOpen(!usefulOpen)}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-links-line text-sm leading-none" />
              </div>
              Useful Links
              <div className="w-3 h-3 flex items-center justify-center">
                <i
                  className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
                    usefulOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>

            {usefulOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-48 pt-2">
                <div className="py-2 bg-background-50 border border-primary-900/30 rounded-lg shadow-lg animate-dropdown">
                  {usefulLinks.map((link) => (
                    <Link
                      key={link.label}
                      to={link.href}
                      className={`block px-4 py-2 text-sm tracking-wide transition-colors cursor-pointer ${
                        isActive(link.href)
                          ? "text-primary-500 font-semibold bg-primary-500/10"
                          : "text-foreground-700 hover:text-primary-500 hover:bg-primary-500/5"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CTA Buttons */}
          <Link
            to="/listings"
            className="ml-2 flex items-center gap-1.5 text-xs xl:text-sm font-semibold px-3 xl:px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer border text-emerald-600 border-emerald-500/50 hover:bg-emerald-500/10 hover:border-emerald-400"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-search-eye-line text-sm leading-none" />
            </div>
            Property Opportunities
          </Link>

          <Link
            to="/submit-property"
            className="flex items-center gap-1.5 text-xs xl:text-sm font-semibold px-3 xl:px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-add-line text-sm leading-none" />
            </div>
            Submit Property
          </Link>

          <Link
            to="/members"
            className="ml-1 flex items-center gap-1.5 text-xs xl:text-sm font-semibold text-foreground-950 bg-primary-400 hover:bg-primary-500 px-3 xl:px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-line text-sm leading-none" />
            </div>
            Members Login
          </Link>
        </div>

        {/* Mobile toggle */}
        <div className="lg:hidden flex items-center gap-2 ml-auto">
          <Link
            to="/listings"
            className="flex items-center gap-1 text-xs font-semibold text-emerald-600 border border-emerald-500/50 px-2.5 py-1.5 rounded-lg whitespace-nowrap cursor-pointer"
          >
            <i className="ri-search-eye-line text-xs" />
            <span className="hidden sm:inline">Property Opportunities</span>
          </Link>
          <button
            className="text-foreground-950 w-8 h-8 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-100/98 backdrop-blur-xl border-t border-primary-900/30 px-4 py-6 space-y-1">
          {/* Home */}
          <Link
            to="/"
            className={mobileLinkClass(isActive("/"))}
          >
            <div className="w-5 h-5 flex items-center justify-center text-primary-500/70">
              <i className="ri-home-line text-base" />
            </div>
            Home
          </Link>

          {/* Mobile Profile - collapsible */}
          <button
            onClick={() => setMobileProfileOpen(!mobileProfileOpen)}
            className={`w-full flex items-center gap-3 py-3 px-2 text-sm tracking-wide rounded-lg transition-colors cursor-pointer ${
              isProfileActive()
                ? "text-primary-500 font-medium bg-primary-500/10"
                : "text-foreground-700 hover:text-primary-500 hover:bg-primary-500/5"
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center text-primary-500/70">
              <i className="ri-user-line text-base" />
            </div>
            Profile
            <div className="ml-auto w-4 h-4 flex items-center justify-center">
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                  mobileProfileOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </button>

          {mobileProfileOpen && (
            <div className="ml-3 border-l border-primary-900/20 space-y-1 pl-3">
              {profileLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`block py-2.5 px-2 text-sm tracking-wide rounded-lg transition-colors cursor-pointer ${
                    isActive(link.href)
                      ? "text-primary-500 font-medium bg-primary-500/10"
                      : "text-foreground-700 hover:text-primary-500 hover:bg-primary-500/5"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}

          {/* Remaining main links (after Profile) */}
          {mainNavLinks.filter((l) => l.href !== "/" && (l.href !== "/karnataka-cities" || settings.features.karnataka_cities_page !== false)).map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className={mobileLinkClass(isActive(link.href))}
            >
              <div className="w-5 h-5 flex items-center justify-center text-primary-500/70">
                <i className={`${link.icon} text-base`} />
              </div>
              {link.label}
            </Link>
          ))}

          {/* Mobile Useful Links - collapsible */}
          <button
            onClick={() => setMobileUsefulOpen(!mobileUsefulOpen)}
            className={`w-full flex items-center gap-3 py-3 px-2 text-sm tracking-wide rounded-lg transition-colors cursor-pointer ${
              isUsefulActive()
                ? "text-primary-500 font-medium bg-primary-500/10"
                : "text-foreground-700 hover:text-primary-500 hover:bg-primary-500/5"
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center text-primary-500/70">
              <i className="ri-links-line text-base" />
            </div>
            Useful Links
            <div className="ml-auto w-4 h-4 flex items-center justify-center">
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                  mobileUsefulOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </button>

          {mobileUsefulOpen && (
            <div className="ml-3 border-l border-primary-900/20 space-y-1 pl-3">
              {usefulLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`block py-2.5 px-2 text-sm tracking-wide rounded-lg transition-colors cursor-pointer ${
                    isActive(link.href)
                      ? "text-primary-500 font-medium bg-primary-500/10"
                      : "text-foreground-700 hover:text-primary-500 hover:bg-primary-500/5"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}

          <div className="my-5 border-t border-primary-900/20" />
          <p className="text-xs font-semibold text-foreground-600 uppercase tracking-widest px-2 mb-3">Quick Actions</p>
          <div className="flex flex-col gap-3">
            <Link
              to="/listings"
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-emerald-500/40 bg-emerald-500/5 text-emerald-600 hover:bg-emerald-500/10 transition-all cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/15 shrink-0">
                <i className="ri-search-eye-line text-base" />
              </div>
              <div>
                <p className="text-sm font-semibold">Property Opportunities</p>
                <p className="text-xs text-emerald-600/70">Browse available listings</p>
              </div>
              <div className="ml-auto w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-base" />
              </div>
            </Link>
            <Link
              to="/submit-property"
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-primary-500 text-background-50 hover:bg-primary-600 transition-all cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-400/30 shrink-0">
                <i className="ri-map-pin-add-line text-base" />
              </div>
              <div>
                <p className="text-sm font-semibold">Submit Property</p>
                <p className="text-xs text-background-50/70">Get your feasible AI report</p>
              </div>
              <div className="ml-auto w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-base" />
              </div>
            </Link>
            <Link
              to="/members"
              className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-primary-400 text-foreground-950 font-semibold text-sm hover:bg-primary-500 transition-all cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-line text-base leading-none" />
              </div>
              Members Login
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}